import { cn } from '@lib/utils';
import { Wallet } from 'lucide-react';
import { Link } from 'react-router-dom';


import { Tooltip } from './Tooltip';

export interface WalletBalanceWidgetProps {
  balance: number;
  className?: string;
  to?: string;
}

function formatRupee(value: number): string {
  // Coerce defensively: the prop is typed `number`, but wallet balances come
  // from a Postgres numeric column and can arrive as a string. Calling
  // toLocaleString on a string silently ignores the digit options and prints
  // the raw 4-decimal database value.
  const n = Number(value) || 0;
  return `₹${n.toLocaleString('en-IN', {
    minimumFractionDigits: n % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export function WalletBalanceWidget({
  balance,
  className,
  to = '/billing',
}: WalletBalanceWidgetProps): JSX.Element {
  const tone: 'ok' | 'warn' | 'critical' =
    balance < 100 ? 'critical' : balance < 500 ? 'warn' : 'ok';

  const tint: Record<typeof tone, string> = {
    ok: 'bg-apple-gray-100 hover:bg-apple-gray-200',
    warn: 'bg-gradient-to-r from-[#FFFBEB] to-[#FEF3C7] hover:brightness-95',
    critical: 'bg-gradient-to-r from-[#FFF1F2] to-[#FFE4E6] hover:brightness-95',
  };

  const iconBg: Record<typeof tone, string> = {
    ok: 'bg-gradient-primary text-white',
    warn: 'bg-gradient-warning text-white',
    critical: 'bg-gradient-danger text-white',
  };

  const textClass: Record<typeof tone, string> = {
    ok: 'text-gradient-primary',
    warn: 'text-amber-700',
    critical: 'text-rose-700',
  };

  return (
    <Tooltip content="Click to add money">
      <Link
        to={to}
        className={cn(
          'group inline-flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 transition-all duration-200 ease-apple',
          tint[tone],
          className,
        )}
      >
        <span
          className={cn(
            'flex h-7 w-7 items-center justify-center rounded-lg shadow-btn-green',
            iconBg[tone],
          )}
        >
          <Wallet size={14} strokeWidth={1.75} />
        </span>
        <span
          className={cn(
            'text-[14px] font-semibold tracking-apple-card tabular-nums',
            textClass[tone],
            tone === 'ok' && 'bg-clip-text text-transparent bg-gradient-primary',
          )}
        >
          {formatRupee(balance)}
        </span>
      </Link>
    </Tooltip>
  );
}
